import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  fetchAuditSummary,
  fetchAuditFilterOptions,
  fetchAuditPage,
  fetchClinicsSummary,
  fetchClinicsFilterOptions,
  fetchClinicsPage,
  fetchDoctorsSummary,
  fetchDoctorsFilterOptions,
  fetchDoctorsPage,
  fetchStaffSummary,
  fetchStaffFilterOptions,
  fetchStaffPage,
  fetchReports,
  fetchCompliance,
  fetchMasterForms,
  fetchProcedures,
  fetchLabReference,
  fetchDrugsSummary,
  fetchDrugsFilterOptions,
  fetchDrugsPage,
  fetchPatientsSummary,           // ← NEW
  fetchPatientsFilterOptions,     // ← NEW
  fetchPatientsPage,              // ← NEW
  fetchNotificationsSummary,    // ← NEW
  fetchNotificationsPage,       // ← NEW
  fetchSettings,
  fetchDataCorrection,
} from '@api/client';
import { QK } from '@api/queries';
import styles from './Sidebar.module.css';

/* ════════════════════════════════════════════════════════════════════
   Sidebar navigation. Each entry maps to a lazy-loaded route.
   Hovering over a link prefetches the matching React Query data
   so the section renders instantly when clicked.
   ════════════════════════════════════════════════════════════════════ */

const NAV = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: 'grid-1x2-fill', text: 'Dashboard' },
      { to: '/clinics', icon: 'hospital', text: 'Clinics' },
      { to: '/reports', icon: 'bar-chart', text: 'Cross-clinic reports' },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/doctors',  icon: 'person-badge', text: 'Doctors' },
      { to: '/patients', icon: 'person-heart', text: 'Patients' },
      { to: '/staff',    icon: 'people',       text: 'Staff' },
      { to: '/kyc',      icon: 'person-vcard', text: 'KYC / Onboarding' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { to: '/forms',      icon: 'file-earmark-text', text: 'Master Forms' },
      { to: '/procedures', icon: 'flower1',           text: 'Procedures' },
      { to: '/labs',       icon: 'eyedropper',        text: 'Lab Reference' },
      { to: '/drugs',      icon: 'capsule',           text: 'Drug Master' },
    ],
  },
  {
    label: 'Audit',
    items: [
      { to: '/notifications',   icon: 'bell',           text: 'Notifications' },
      { to: '/data-correction', icon: 'tools',          text: 'Data correction' },
      { to: '/audit',           icon: 'clipboard-check', text: 'Audit log' },
      { to: '/compliance',      icon: 'calendar-check', text: 'Compliance' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/settings', icon: 'gear', text: 'Settings' },
    ],
  },
];

/**
 * Prefetcher map. When the user hovers a nav link, we kick off the
 * queries that section will need so they're cached before the click.
 */
const PREFETCH = {
  '/dashboard': (qc) => {
    qc.prefetchQuery({ queryKey: QK.clinics.summary, queryFn: fetchClinicsSummary });
    qc.prefetchQuery({ queryKey: QK.reports.bundle(null), queryFn: () => fetchReports(null) });
  },
  '/audit': (qc) => {
    qc.prefetchQuery({ queryKey: QK.audit.summary, queryFn: fetchAuditSummary });
    qc.prefetchQuery({ queryKey: QK.audit.filters, queryFn: fetchAuditFilterOptions });
    qc.prefetchInfiniteQuery({
      queryKey: QK.audit.list({}),
      queryFn: ({ pageParam }) => fetchAuditPage({ cursor: pageParam, limit: 50 }),
      initialPageParam: undefined,
    });
  },
  '/clinics': (qc) => {
    qc.prefetchQuery({ queryKey: QK.clinics.summary, queryFn: fetchClinicsSummary });
    qc.prefetchQuery({ queryKey: QK.clinics.filters, queryFn: fetchClinicsFilterOptions });
    qc.prefetchInfiniteQuery({
      queryKey: QK.clinics.list({}),
      queryFn: ({ pageParam }) => fetchClinicsPage({ cursor: pageParam, limit: 50 }),
      initialPageParam: undefined,
    });
  },
  '/doctors': (qc) => {
    qc.prefetchQuery({ queryKey: QK.doctors.summary, queryFn: fetchDoctorsSummary });
    qc.prefetchQuery({ queryKey: QK.doctors.filters, queryFn: fetchDoctorsFilterOptions });
    qc.prefetchInfiniteQuery({
      queryKey: QK.doctors.list({}),
      queryFn: ({ pageParam }) => fetchDoctorsPage({ cursor: pageParam, limit: 50 }),
      initialPageParam: undefined,
    });
  },
  '/staff': (qc) => {
    qc.prefetchQuery({ queryKey: QK.staff.summary, queryFn: fetchStaffSummary });
    qc.prefetchQuery({ queryKey: QK.staff.filters, queryFn: fetchStaffFilterOptions });
    qc.prefetchInfiniteQuery({
      queryKey: QK.staff.list({}),
      queryFn: ({ pageParam }) => fetchStaffPage({ cursor: pageParam, limit: 50 }),
      initialPageParam: undefined,
    });
  },
  '/reports': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.reports.bundle(null),
      queryFn: () => fetchReports(null),
    });
  },
  '/compliance': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.compliance.year(null),
      queryFn: () => fetchCompliance(null),
    });
  },
  '/forms': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.masterForms.all,
      queryFn: fetchMasterForms,
    });
  },
  '/procedures': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.procedures.all,
      queryFn: fetchProcedures,
    });
  },
  '/labs': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.labReference.all,
      queryFn: fetchLabReference,
    });
  },
  '/drugs': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.drugs.summary,
      queryFn: fetchDrugsSummary,
    });
    qc.prefetchQuery({
      queryKey: QK.drugs.filterOptions,
      queryFn: fetchDrugsFilterOptions,
    });
    qc.prefetchInfiniteQuery({
      queryKey: QK.drugs.page({}),
      queryFn: () => fetchDrugsPage({}),
      initialPageParam: undefined,
    });
  },
  '/data-correction': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.dataCorrection.all,
      queryFn: fetchDataCorrection,
    });
  },
  '/patients': (qc) => {
    qc.prefetchQuery({ queryKey: QK.patients.summary, queryFn: fetchPatientsSummary });
    qc.prefetchQuery({ queryKey: QK.patients.filters, queryFn: fetchPatientsFilterOptions });
    qc.prefetchInfiniteQuery({
      queryKey: QK.patients.list({}),
      queryFn: ({ pageParam }) => fetchPatientsPage({ cursor: pageParam, limit: 50 }),
      initialPageParam: undefined,
    });
  },
  '/notifications': (qc) => {
    qc.prefetchQuery({ queryKey: QK.notifications.summary, queryFn: fetchNotificationsSummary });
    qc.prefetchInfiniteQuery({
      queryKey: QK.notifications.list({}),
      queryFn: ({ pageParam }) => fetchNotificationsPage({ cursor: pageParam, limit: 25 }),
      initialPageParam: undefined,
    });
  },
  '/settings': (qc) => {
    qc.prefetchQuery({
      queryKey: QK.settings.all,
      queryFn: fetchSettings,
    });
  },
  // Add more sections as they get wired:
  // '/doctors':  (qc) => qc.prefetchQuery(...),
  // '/staff':    (qc) => qc.prefetchQuery(...),
};

export default function Sidebar({ open = false, onClose }) {
  const qc = useQueryClient();

  const handlePrefetch = (path) => () => {
    const fn = PREFETCH[path];
    if (fn) fn(qc);
  };

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('1ayurved_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('1ayurved_sidebar_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${open ? styles.open : ''}`}>
      <div className={styles.header}>
        <div className={styles.brandText}>
          <div className={styles.shloka}>"स्वस्थस्य स्वास्थ्यरक्षणम्"</div>
          <div className={styles.shlokaSource}>Protect the health of the healthy</div>
        </div>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={toggle}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} aria-hidden="true" />
        </button>
      </div>

      <nav aria-label="Primary">
        {NAV.map((group) => (
          <div key={group.label}>
            <div className={styles.section}>{group.label}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.text}
                onClick={onClose}
                onMouseEnter={handlePrefetch(item.to)}
                onFocus={handlePrefetch(item.to)}
                className={({ isActive }) =>
                  `${styles.item} ${isActive ? styles.itemActive : ''}`
                }
              >
                <span className={styles.icon}><i className={`bi bi-${item.icon}`} aria-hidden="true" /></span>
                <span className={styles.label}>{item.text}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
